// jsonFilterEmit — per-engine JSON WHERE-predicate emit for the VQE
// FilterBuilder's path-aware payload.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice D3.
//
// FilterBuilder (D2) surfaces an optional `path` field on the form payload when
// the selected column is JSON-classified. D3 turns that payload into a
// dialect-correct predicate string before SR-Edit's `add-where` primitive sees
// it. No new emit primitive — `commitEdit('add-where', text)` consumes the
// string verbatim per the existing contract.
//
// The dispatch is per-engine + per-operator: each engine's lattice (Slice C2)
// defines which operators take a path. For path-templated operators we emit
// the function-call / arrow-operator shape; for non-path operators (`=`, `<>`,
// `IS NULL`, …) the path is ignored and we fall back to the simple
// `col op value` shape that non-JSON columns already use.

import type { SqlEngine } from '../../../../sql-grammar/model';

export type JsonFilterInputs = {
  readonly column: string;
  readonly operator: string;
  readonly value: string;
  readonly path: string;
};

export function formatJsonFilterText(inputs: JsonFilterInputs, engine: SqlEngine): string {
  switch (engine) {
    case 'postgres':
      return formatPostgresJsonFilter(inputs);
    case 'mssql':
      return formatMssqlJsonFilter(inputs);
    case 'mysql':
      return formatMysqlJsonFilter(inputs);
    case 'oracle':
      return formatOracleJsonFilter(inputs);
    case 'sqlite':
      return formatSqliteJsonFilter(inputs);
  }
}

function formatPostgresJsonFilter({ column, operator, value, path }: JsonFilterInputs): string {
  switch (operator) {
    case '->':
    case '->>': {
      const lhs = `${column} ${operator} ${sqlQuote(stripDollarPath(path))}`;
      return appendValueOrBare(lhs, value);
    }
    case '#>':
    case '#>>': {
      const segments = pathToSegments(path);
      const lhs = `${column} ${operator} '{${segments.join(',')}}'`;
      return appendValueOrBare(lhs, value);
    }
    case '@>':
    case '<@':
      return `${column} ${operator} ${sqlQuote(value)}::jsonb`;
    case '?':
      return `${column} ? ${sqlQuote(stripDollarPath(path))}`;
    case '?|':
    case '?&':
      return `${column} ${operator} ARRAY[${value}]`;
    case 'jsonb_path_exists':
      return `jsonb_path_exists(${column}, ${sqlQuote(path)})`;
    default:
      return fallbackSimple(column, operator, value);
  }
}

function formatMssqlJsonFilter({ column, operator, value, path }: JsonFilterInputs): string {
  switch (operator) {
    case 'JSON_VALUE':
      return `JSON_VALUE(${column}, ${sqlQuote(path)}) = ${value}`;
    case 'JSON_QUERY':
      return `JSON_QUERY(${column}, ${sqlQuote(path)}) IS NOT NULL`;
    case 'JSON_PATH_EXISTS':
      return `JSON_PATH_EXISTS(${column}, ${sqlQuote(path)}) = 1`;
    case 'ISJSON':
      return `ISJSON(${column}) > 0`;
    case 'EXISTS OPENJSON':
      return `EXISTS (SELECT 1 FROM OPENJSON(${column}) WHERE ${value})`;
    default:
      return fallbackSimple(column, operator, value);
  }
}

function formatMysqlJsonFilter({ column, operator, value, path }: JsonFilterInputs): string {
  switch (operator) {
    case '->':
    case '->>': {
      const lhs = `${column} ${operator} ${sqlQuote(path)}`;
      return appendValueOrBare(lhs, value);
    }
    case 'JSON_CONTAINS':
      return `JSON_CONTAINS(${column}, ${sqlQuote(value)}, ${sqlQuote(path)})`;
    case 'JSON_CONTAINS_PATH':
      return `JSON_CONTAINS_PATH(${column}, 'one', ${sqlQuote(path)})`;
    case 'JSON_OVERLAPS':
      return `JSON_OVERLAPS(${column}, ${sqlQuote(value)})`;
    case 'JSON_VALID':
      return `JSON_VALID(${column})`;
    case 'MEMBER OF':
      return `${sqlQuote(value)} MEMBER OF (${column})`;
    default:
      return fallbackSimple(column, operator, value);
  }
}

function formatOracleJsonFilter({ column, operator, value, path }: JsonFilterInputs): string {
  switch (operator) {
    case 'JSON_VALUE':
      return `JSON_VALUE(${column}, ${sqlQuote(path)}) = ${value}`;
    case 'JSON_QUERY':
      return `JSON_QUERY(${column}, ${sqlQuote(path)}) IS NOT NULL`;
    case 'JSON_EXISTS':
      return `JSON_EXISTS(${column}, ${sqlQuote(path)})`;
    case 'IS JSON':
    case 'IS NOT JSON':
      return `${column} ${operator}`;
    case '.': {
      const dotted = path.startsWith('.') ? path : `.${stripDollarPath(path)}`;
      const lhs = `${column}${dotted}`;
      return appendValueOrBare(lhs, value);
    }
    default:
      return fallbackSimple(column, operator, value);
  }
}

function formatSqliteJsonFilter({ column, operator, value, path }: JsonFilterInputs): string {
  switch (operator) {
    case '->':
    case '->>': {
      const lhs = `${column} ${operator} ${sqlQuote(path)}`;
      return appendValueOrBare(lhs, value);
    }
    case 'json_extract':
      return `json_extract(${column}, ${sqlQuote(path)}) = ${value}`;
    case 'json_valid':
      return `json_valid(${column})`;
    default:
      return fallbackSimple(column, operator, value);
  }
}

function sqlQuote(text: string): string {
  return `'${text.replace(/'/g, "''")}'`;
}

function stripDollarPath(path: string): string {
  // Accept `$.user.email` / `$user` / `user` — return the part after the
  // leading `$.` or `$` so PG `->` keys / Oracle dot-notation are user-friendly.
  return path.replace(/^\$\.?/, '');
}

function pathToSegments(path: string): readonly string[] {
  // Accept `$.a.b`, `a.b`, or `{a,b}` — return ['a','b']. Used by PG `#>` and
  // `#>>` which take a path-segment array literal.
  const cleaned = path.replace(/^\$\.?/, '').replace(/^\{|\}$/g, '');
  return cleaned.split(/[.,]/g).filter((s) => s.length > 0);
}

function appendValueOrBare(lhs: string, value: string): string {
  return value.length > 0 ? `${lhs} = ${value}` : lhs;
}

function fallbackSimple(column: string, operator: string, value: string): string {
  return value.length > 0 ? `${column} ${operator} ${value}` : `${column} ${operator}`;
}
